const EXPORT_SECTION_ID = 'dailyfeed-export';
const PAGE_SIZE = 250;
const CONCURRENCY = 3;

const CSV_COLUMNS = [
  ['collection', 'Collection'],
  ['product_title', 'Product Title'],
  ['brand', 'Brand'],
  ['model', 'Model'],
  ['product_type', 'Product Type'],
  ['web_categories', 'Web categories'],
  ['variant_title', 'Variant Title'],
  ['sku', 'SKU'],
  ['upc', 'UPC'],
  ['price', 'Price'],
  ['msrp', 'MSRP'],
  ['stock', 'Stock'],
  ['inventory_quantity', 'Inventory Quantity'],
  ['available', 'Available'],
  ['product_url', 'Product URL'],
  ['image_url', 'Image URL'],
];

function setButtonState(button, state, label) {
  const text = button.querySelector('.dailyfeed-download-button__text, .vc-dailyfeed-button-text');
  button.classList.toggle('is-loading', state === 'loading');
  button.classList.toggle('is-complete', state === 'complete');
  button.classList.toggle('is-error', state === 'error');
  button.disabled = state === 'loading';
  button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
  if (text && label) text.textContent = label;
}

function resetButton(button) {
  setButtonState(button, 'idle', button.dataset.dailyfeedDefaultLabel || 'Download this collection');
}

function buildExportUrl(page) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', EXPORT_SECTION_ID);
  url.searchParams.set('page', String(page));
  url.searchParams.set('view', '');
  url.searchParams.delete('view');
  return url;
}

function parseExportPayload(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const script = doc.querySelector('[data-dailyfeed-export]');
  if (!script) throw new Error('Dailyfeed export section was not found.');
  return JSON.parse(script.textContent);
}

async function fetchExportPage(page) {
  const response = await fetch(buildExportUrl(page).toString(), {
    headers: { Accept: 'text/html' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Dailyfeed export failed on page ${page}.`);
  return parseExportPayload(await response.text());
}

async function mapWithConcurrency(items, limit, callback, onProgress) {
  const results = new Array(items.length);
  let cursor = 0;
  let completed = 0;

  async function worker() {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await callback(items[index]);
      completed += 1;
      if (onProgress) onProgress(completed, items.length);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// ExcelJS (~930KB) loads only when a download actually starts \u2014 never on page load.
let excelJsPromise = null;
function loadExcelJs(src) {
  if (window.ExcelJS) return Promise.resolve();
  if (!excelJsPromise) {
    excelJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = () => {
        excelJsPromise = null;
        reject(new Error('Failed to load the spreadsheet library.'));
      };
      document.head.appendChild(script);
    });
  }
  return excelJsPromise;
}

// Palette and layout mirror Cameron's dailyfeed template
// (dailyfeed-category-power-wall-chargers-2026-08-12.xlsx).
const TEMPLATE = {
  font: 'Inter',
  headerFill: 'FF104862',
  brandRed: 'FFC00000',
  brandLine: 'VALOR COMMUNICATION',
};

const COLUMN_LAYOUT = [
  { key: 'spacer', width: 3.29 },
  { key: 'image_formula', label: 'Product Image', width: 13.86 },
  { key: 'collection', width: 42 },
  { key: 'product_title', width: 81.86, wrap: true },
  { key: 'brand', width: 14.14 },
  { key: 'model', width: 53.86, wrap: true },
  { key: 'product_type', width: 15.43 },
  { key: 'web_categories', width: 20 },
  { key: 'variant_title', width: 19.57 },
  { key: 'sku', width: 27.43 },
  { key: 'upc', width: 18.14 },
  { key: 'price', width: 9.29 },
  { key: 'msrp', width: 9.29 },
  { key: 'stock', width: 15.29 },
  { key: 'inventory_quantity', width: 20.86 },
  { key: 'available', width: 11 },
  { key: 'product_url', width: 73, wrap: true },
  { key: 'image_url', width: 78.86, wrap: true },
];

function thumbUrl(imageUrl) {
  if (!imageUrl) return '';
  const base = String(imageUrl).split('?')[0];
  return `${base}?width=180`;
}

async function buildWorkbook(rows, sourceHandle, sourceTitle) {
  const workbook = new window.ExcelJS.Workbook();
  const sheetName = `dailyfeed-${sourceHandle || 'catalog'}`
    .replace(/[\\/*?:[\]]/g, ' ')
    .slice(0, 31)
    .trim();
  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 5 }],
  });

  const labelByKey = Object.fromEntries(CSV_COLUMNS);
  sheet.columns = COLUMN_LAYOUT.map(({ key, width }) => ({ key, width }));

  const bodyFont = { name: TEMPLATE.font, size: 11 };

  const brandCell = sheet.getCell('B2');
  brandCell.value = TEMPLATE.brandLine;
  brandCell.font = { name: TEMPLATE.font, size: 9, bold: true, color: { argb: TEMPLATE.brandRed } };

  const titleCell = sheet.getCell('B3');
  titleCell.value = (rows[0] && rows[0].collection) || sourceTitle || '';
  titleCell.font = { name: TEMPLATE.font, size: 14, bold: true };
  sheet.getRow(3).height = 22.5;

  const headerRow = sheet.getRow(5);
  COLUMN_LAYOUT.forEach(({ key, label }, i) => {
    if (key === 'spacer') return;
    const cell = headerRow.getCell(i + 1);
    cell.value = label || labelByKey[key] || key;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TEMPLATE.headerFill } };
    cell.font = { name: TEMPLATE.font, size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
  });

  rows.forEach((row, index) => {
    const excelRow = sheet.getRow(6 + index);
    excelRow.height = 66;
    COLUMN_LAYOUT.forEach(({ key, wrap }) => {
      if (key === 'spacer' || key === 'image_formula') return;
      const cell = excelRow.getCell(key);
      let value = row[key] == null ? '' : row[key];
      if (key === 'price' || key === 'msrp' || key === 'inventory_quantity' || key === 'upc') {
        const num = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
        if (value !== '' && !Number.isNaN(num)) value = num;
      }
      if (key === 'available' && /^(true|false)$/i.test(String(value))) {
        value = /^true$/i.test(String(value));
      }
      cell.value = value;
      cell.font = bodyFont;
      cell.alignment = { vertical: 'middle', wrapText: Boolean(wrap) };
      if (key === 'upc' && typeof value === 'number') {
        cell.numFmt = '0';
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      }
    });
    const thumb = thumbUrl(row.image_url);
    if (thumb) {
      // _xlfn. prefix is how post-2019 functions are stored in xlsx; renders as
      // a live thumbnail in Excel 365 / Google Sheets, #NAME? on older Excel \u2014
      // the plain Image URL column stays as the fallback. Cameron's template
      // stores the evaluated form of the same thing (web-image rich values).
      const imgCell = excelRow.getCell('image_formula');
      imgCell.value = { formula: `_xlfn.IMAGE("${thumb}")` };
      imgCell.font = bodyFont;
    }
  });

  return workbook;
}

async function downloadXlsx(rows, sourceHandle, sourceTitle) {
  const workbook = await buildWorkbook(rows, sourceHandle, sourceTitle);
  const buffer = await workbook.xlsx.writeBuffer();
  const date = new Date().toISOString().slice(0, 10);
  const safeHandle = sourceHandle || 'collection';
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `dailyfeed-${safeHandle}-${date}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

async function handleDownload(button) {
  try {
    setButtonState(button, 'loading', 'Preparing...');

    const excelJsReady = loadExcelJs(button.dataset.exceljsSrc);
    const firstPage = await fetchExportPage(1);
    const totalPages = Math.max(1, Number(firstPage.pagination?.pages || 1));
    const payloads = [firstPage];

    if (totalPages > 1) {
      setButtonState(button, 'loading', `Preparing 1/${totalPages}`);
      const pages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
      const rest = await mapWithConcurrency(
        pages,
        CONCURRENCY,
        fetchExportPage,
        (done) => setButtonState(button, 'loading', `Preparing ${done + 1}/${totalPages}`)
      );
      payloads.push(...rest);
    }

    let rows = payloads.flatMap((payload) => payload.rows || []);
    // Coming Soon / New Arrivals narrow the grid client-side (?arrival_date=,
    // &arrival_type=), which the export section can't see - apply the same filter.
    const exportFilter = window.__vaExportFilter;
    if (typeof exportFilter === 'function') {
      rows = rows.filter((row) => row.product_id == null || exportFilter(row.product_id));
    }
    const sourceHandle =
      firstPage.source?.handle || firstPage.collection?.handle || button.dataset.collectionHandle || 'collection';
    const sourceTitle = firstPage.source?.title || firstPage.collection?.title || sourceHandle;
    await excelJsReady;
    await downloadXlsx(rows, sourceHandle, sourceTitle);

    setButtonState(button, 'complete', `Downloaded ${rows.length}`);
    window.setTimeout(() => resetButton(button), 1800);
  } catch (error) {
    console.error(error);
    setButtonState(button, 'error', 'Download failed');
    window.setTimeout(() => resetButton(button), 2600);
  }
}

function initDailyfeedButtons() {
  document.querySelectorAll('[data-dailyfeed-download]').forEach((button) => {
    if (button.dataset.dailyfeedBound === 'true') return;
    button.dataset.dailyfeedBound = 'true';
    button.addEventListener('click', () => handleDownload(button));
  });
}

initDailyfeedButtons();
document.addEventListener('shopify:section:load', initDailyfeedButtons);
